import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import { CanvasFrameAPI, CircleAPI, CommonControlsPropsAPI, CounterAPI, DefaultTimerAPI, DrawAPI, GridAPI, IndicatorAPI, ModelPropsAPI, ModelStateAPI, RadioAPI, RangeAPI, RoundRectangleAPI, SelectAPI, SeriesAPI, TimerAPI, TimeSeriesAPI, UpdateDataAPI, WithControlsAPI, WithFrameAPI } from '../components/api';
export const _frontmatter = {
  "Title": "API reference"
};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <p>{`A single-page reference of all the APIs used throughout `}<inlineCode parentName="p">{`React-Sim`}</inlineCode>{`.`}</p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/react-sim/api#model"
        }}>{`Model`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/react-sim/api#withframe"
        }}>{`withFrame`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/react-sim/api#grid"
        }}>{`grid`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/react-sim/api#canvasframe"
        }}>{`canvasFrame`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/react-sim/api#timeseries"
        }}>{`timeSeries`}</a></li>
    </ul>
    <h1 {...{
      "id": "model"
    }}>{`Model`}</h1>
    <h2 {...{
      "id": "usage"
    }}>{`Usage`}</h2>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import {Model} from 'react-sim';

function initData = params => { /* ... */ }
function updateData =
  ({data, params, tick, ...args}) => { /* ... */ }
const params = { /* ... */};
function Frame =
  ({data, params, tick, ...args}) => <>{ /* ... */ }</>;

export function MyModel = () => <Model
  initalParams={params}
  initData={initData}
  updateData={updateData}
>
  <Frame />
</Model>
`}</code></pre>
    <h2 {...{
      "id": "state"
    }}>{`State`}</h2>
    <ModelStateAPI mdxType="ModelStateAPI" />
    <h2 {...{
      "id": "properties"
    }}>{`Properties`}</h2>
    <ModelPropsAPI mdxType="ModelPropsAPI" />
    <p>{`For more information on controls and the syntax of the control object, see `}<a parentName="p" {...{
        "href": "/react-sim/controls"
      }}>{`Controls`}</a>{`.`}</p>
    <h1 {...{
      "id": "frame-helpers"
    }}>{`Frame Helpers`}</h1>
    <h2 {...{
      "id": "withframe"
    }}>{`withFrame`}</h2>
    <h3 {...{
      "id": "usage-1"
    }}>{`Usage`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import {Model, withFrame} from 'react-sim';

const FrameComponent = props => <>{ /* ... */ }</>;
const Frame = withFrame(FrameComponent);

export function MyModel = () => <Model>
  <Frame />
</Model>
`}</code></pre>
    <h3 {...{
      "id": "api"
    }}>{`API`}</h3>
    <p><inlineCode parentName="p">{`withFrame`}</inlineCode>{` is a wrapper around a `}<a parentName="p" {...{
        "href": "/react-sim/frames"
      }}>{`Frame`}</a>{` component.`}</p>
    <p>{`Wrapping a component with `}<inlineCode parentName="p">{`withFrame`}</inlineCode>{` will expose the connected component to the following properties:
These properties come from the state of the `}<inlineCode parentName="p">{`Model`}</inlineCode>{` this component is a descendant of.`}</p>
    <WithFrameAPI mdxType="WithFrameAPI" />
    <p>{`For convenience, a component which is a direct child of a `}<inlineCode parentName="p">{`Model`}</inlineCode>{` automatically receives these properties without `}<inlineCode parentName="p">{`withFrame`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import {Model, withFrame} from 'react-sim';

const FrameComponent = props => <>{ /* ... */ }</>;
const Frame = withFrame(FrameComponent);

export function MyModel = () => <Model>
  {/* this component has access to frame props */}
  <FrameComponent />
  <div>
    {/* this one doesn't, because it's not a direct child */}
    <FrameComponent />
    {/* this component has access to frame props, because it's connected */}
    <Frame />
  </div>
</Model>
`}</code></pre>
    <h2 {...{
      "id": "grid"
    }}>{`Grid`}</h2>
    <p>{`see `}<a parentName="p" {...{
        "href": "/react-sim/frame-helpers/grid"
      }}>{`Grid guide`}</a></p>
    <h3 {...{
      "id": "usage-2"
    }}>{`Usage`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`  import {Model, Grid} from 'react-sim';

  export function MyModel = () => <Model>
    <Grid />
  </Model>
`}</code></pre>
    <h3 {...{
      "id": "api-1"
    }}>{`API`}</h3>
    <GridAPI mdxType="GridAPI" />
    <h3 {...{
      "id": "gridcomponent"
    }}>{`GridComponent`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import { GridComponent } from 'react-sim';
`}</code></pre>
    <p><inlineCode parentName="p">{`Grid`}</inlineCode>{` is wrapped with `}<inlineCode parentName="p">{`withFrame`}</inlineCode>{` so it can be used anywhere in the tree. The corresponding, unwrapped component, `}<inlineCode parentName="p">{`GridComponent`}</inlineCode>{`, can also be exported from `}<inlineCode parentName="p">{`react-sim`}</inlineCode>{`.`}</p>
    <h2 {...{
      "id": "canvasframe"
    }}>{`CanvasFrame`}</h2>
    <p>{`see `}<a parentName="p" {...{
        "href": "/react-sim/frame-helpers/canvas-frame"
      }}>{`CanvasFrame guide`}</a></p>
    <h3 {...{
      "id": "usage-3"
    }}>{`Usage`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`  import {Model, CanvasFrame} from 'react-sim';

  function draw({ctx, data, ...args}) => {
    /* canvas operations */
  }

  export function MyModel = () => <Model>
    <CanvasFrame draw={draw} />
  </Model>
`}</code></pre>
    <h3 {...{
      "id": "api-2"
    }}>{`API`}</h3>
    <CanvasFrameAPI mdxType="CanvasFrameAPI" />
    <h3 {...{
      "id": "circle"
    }}>{`Circle`}</h3>
    <CircleAPI mdxType="CircleAPI" />
    <h3 {...{
      "id": "roundrectangle"
    }}>{`RoundRectangle`}</h3>
    <RoundRectangleAPI mdxType="RoundRectangleAPI" />
    <h3 {...{
      "id": "draw"
    }}>{`Draw`}</h3>
    <DrawAPI mdxType="DrawAPI" />
    <h2 {...{
      "id": "timeseries"
    }}>{`TimeSeries`}</h2>
    <p>{`see `}<a parentName="p" {...{
        "href": "/react-sim/frame-helpers/time-series"
      }}>{`TimeSeries guide`}</a>{`.`}</p>
    <h3 {...{
      "id": "series"
    }}>{`Series`}</h3>
    <SeriesAPI mdxType="SeriesAPI" />
    <h3 {...{
      "id": "timeseries-1"
    }}>{`TimeSeries`}</h3>
    <TimeSeriesAPI mdxType="TimeSeriesAPI" />
    <h3 {...{
      "id": "counter"
    }}>{`Counter`}</h3>
    <CounterAPI mdxType="CounterAPI" />
    <h3 {...{
      "id": "indicator"
    }}>{`Indicator`}</h3>
    <IndicatorAPI mdxType="IndicatorAPI" />
    <h2 {...{
      "id": "controls"
    }}>{`Controls`}</h2>
    <p>{`See `}<a parentName="p" {...{
        "href": "/react-sim/controls"
      }}>{`Controls guide`}</a>{`.`}</p>
    <h3 {...{
      "id": "withcontrols"
    }}>{`withControls`}</h3>
    <p>{`Components wrapped with `}<inlineCode parentName="p">{`withControls`}</inlineCode>{` gain access to the following properties:`}</p>
    <WithControlsAPI mdxType="WithControlsAPI" />
    <h3 {...{
      "id": "common-controls-properties"
    }}>{`Common Controls Properties`}</h3>
    <p>{`Unless otherwise specified, all built-in controls have the following properties:`}</p>
    <CommonControlsPropsAPI mdxType="CommonControlsPropsAPI" />
    <h3 {...{
      "id": "range"
    }}>{`Range`}</h3>
    <p>{`See the `}<a parentName="p" {...{
        "href": "/react-sim/controls/range"
      }}>{`Range guide`}</a>{`.`}</p>
    <RangeAPI mdxType="RangeAPI" />
    <h3 {...{
      "id": "timer"
    }}>{`Timer`}</h3>
    <h4 {...{
      "id": "default-timer"
    }}>{`Default Timer`}</h4>
    <p>{`See the `}<a parentName="p" {...{
        "href": "/react-sim/controls/default-timer"
      }}>{`Default timer guide`}</a>{`.`}</p>
    <p>{`By default a `}<inlineCode parentName="p">{`Model`}</inlineCode>{` comes with a built-in `}<inlineCode parentName="p">{`Timer`}</inlineCode>{` control. For this defaut `}<inlineCode parentName="p">{`Timer`}</inlineCode>{`, the following props come directly from `}<inlineCode parentName="p">{`Model`}</inlineCode>{` (see `}<a parentName="p" {...{
        "href": "/react-sim/api/#model"
      }}>{`Model properties`}</a>{`.)`}</p>
    <DefaultTimerAPI mdxType="DefaultTimerAPI" />
    <h4 {...{
      "id": "general-case"
    }}>{`General case`}</h4>
    <p>{`See the `}<a parentName="p" {...{
        "href": "/react-sim/controls/timer"
      }}>{`Timer guide`}</a>{`.`}</p>
    <p>{`If the `}<inlineCode parentName="p">{`Timer`}</inlineCode>{` is defined anywhere else, these properties can be passed to the `}<inlineCode parentName="p">{`Timer`}</inlineCode>{` directly.`}</p>
    <TimerAPI mdxType="TimerAPI" />
    <h3 {...{
      "id": "checkbox"
    }}>{`Checkbox`}</h3>
    <p>{`See the `}<a parentName="p" {...{
        "href": "/react-sim/controls/checkbox"
      }}>{`Checkbox guide.`}</a></p>
    <p>{`Its API is solely the `}<a parentName="p" {...{
        "href": "/react-sim/api#common-controls-properties"
      }}>{`Common Control Props.`}</a></p>
    <h3 {...{
      "id": "input"
    }}>{`Input`}</h3>
    <p>{`See the `}<a parentName="p" {...{
        "href": "/react-sim/controls/input"
      }}>{`Input guide.`}</a></p>
    <p>{`Its API is solely the `}<a parentName="p" {...{
        "href": "/react-sim/api#common-controls-properties"
      }}>{`Common Control Props.`}</a></p>
    <h3 {...{
      "id": "radio"
    }}>{`Radio`}</h3>
    <p>{`See the `}<a parentName="p" {...{
        "href": "/react-sim/controls/radio"
      }}>{`Radio guide.`}</a></p>
    <RadioAPI mdxType="RadioAPI" />
    <h3 {...{
      "id": "select"
    }}>{`Select`}</h3>
    <p>{`See the `}<a parentName="p" {...{
        "href": "/react-sim/controls/select"
      }}>{`Select guide.`}</a></p>
    <SelectAPI mdxType="SelectAPI" />
    <h3 {...{
      "id": "toggle"
    }}>{`Toggle`}</h3>
    <p>{`See the `}<a parentName="p" {...{
        "href": "/react-sim/controls/toggle"
      }}>{`Toggle guide.`}</a></p>
    <p>{`Its API is solely the `}<a parentName="p" {...{
        "href": "/react-sim/api#common-controls-properties"
      }}>{`Common Control Props.`}</a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      